import { BaseMoney, Cart, DiscountCodeState } from "src/types/ctgraphql.d";
import { DiscountCodeRejectionReason } from "src/types/graphql.d";
import {
  getIsDiscountCodeInLineItems,
  getLineItemsDiscountPrice,
} from "src/utils/ct/discountCodeHelper";
import { getTalonOneDiscounts } from "src/utils/talon-one";

export type AppliedDiscount = {
  code: string;
  kind: "promo" | "storeCredit";
  amount: BaseMoney | null;
};

export type InvalidDiscount = {
  code: string;
  reasonCode?: string | null;
};

type GroupedDiscounts = {
  promos: AppliedDiscount[];
  storeCredit: AppliedDiscount | undefined;
  promoTotal: BaseMoney | null;
};

type DiscountSource = Pick<
  Cart,
  "discountCodes" | "lineItems" | "custom" | "totalPrice"
>;

export const isStoreCreditCode = (code?: string | null): boolean =>
  code?.toUpperCase().startsWith("STORECREDIT") ?? false;

export const getDiscountKind = (code: string): AppliedDiscount["kind"] =>
  isStoreCreditCode(code) ? "storeCredit" : "promo";

export const hasRejectedTalonOneDiscount = (
  cart: Pick<Cart, "custom"> | null | undefined
): boolean =>
  getTalonOneDiscounts(cart?.custom).some(({ state }) => state === "rejected");

export function getAppliedDiscounts({
  discountCodes,
  lineItems,
  custom,
  totalPrice,
}: DiscountSource): AppliedDiscount[] {
  const talonOneDiscounts = getTalonOneDiscounts(custom)
    .filter(({ state }) => state === "accepted")
    .map(
      ({ code, lineItemDiscountsTotal }): AppliedDiscount => ({
        code,
        kind: getDiscountKind(code),
        amount:
          lineItemDiscountsTotal && totalPrice
            ? {
                centAmount: lineItemDiscountsTotal,
                currencyCode: totalPrice.currencyCode as string,
                fractionDigits: 2,
                type: "centPrecision",
              }
            : null,
      })
    );

  const ctDiscounts = (discountCodes ?? []).flatMap(
    (discountCodeInfo): AppliedDiscount[] => {
      const code = discountCodeInfo.discountCode?.code;

      if (!code || discountCodeInfo.state !== DiscountCodeState.MatchesCart) {
        return [];
      }

      return [
        {
          code,
          kind: getDiscountKind(code),
          amount: getLineItemsDiscountPrice(
            [discountCodeInfo],
            lineItems ?? []
          ),
        },
      ];
    }
  );

  const seen = new Set<string>();

  return [...talonOneDiscounts, ...ctDiscounts].filter(({ code }) => {
    if (seen.has(code)) {
      return false;
    }

    seen.add(code);

    return true;
  });
}

export function getInvalidDiscounts({
  discountCodes,
  lineItems,
  shippingInfo,
  custom,
}: Pick<
  Cart,
  "discountCodes" | "lineItems" | "shippingInfo" | "custom"
>): InvalidDiscount[] {
  const ctInvalidCodes = (discountCodes ?? []).flatMap(
    (discountCodeInfo): InvalidDiscount[] => {
      const { discountCode, state } = discountCodeInfo;
      const code = discountCode?.code;
      const targets = discountCode?.cartDiscounts ?? [];

      const hasLineItemsEffect =
        targets.some(({ target }) => target?.type !== "shipping") &&
        getIsDiscountCodeInLineItems(lineItems ?? [], discountCodeInfo);
      const hasShippingEffect =
        targets.some(({ target }) => target?.type === "shipping") &&
        !!shippingInfo?.discountedPrice;

      const isInvalid =
        state !== DiscountCodeState.MatchesCart ||
        (!hasLineItemsEffect && !hasShippingEffect);

      const reasonCode =
        state === DiscountCodeState.NotValid
          ? DiscountCodeRejectionReason.CouponExpired
          : null;

      return code && isInvalid ? [{ code, reasonCode }] : [];
    }
  );

  const talonOneRejectedCodes = getTalonOneDiscounts(custom)
    .filter(({ state }) => state === "rejected")
    .map(({ code, rejectionReason }) => ({
      code,
      reasonCode: rejectionReason,
    }));

  return [...ctInvalidCodes, ...talonOneRejectedCodes];
}

const sumMoney = (amounts: (BaseMoney | null)[]): BaseMoney | null => {
  const present = amounts.filter((amount): amount is BaseMoney => !!amount);

  if (!present.length) {
    return null;
  }

  return {
    ...present[0],
    centAmount: present.reduce(
      (total, { centAmount }) => total + (centAmount as number),
      0
    ),
  };
};

export function groupDiscounts(discounts: AppliedDiscount[]): GroupedDiscounts {
  const promos = discounts.filter(({ kind }) => kind === "promo");

  return {
    promos,
    storeCredit: discounts.find(({ kind }) => kind === "storeCredit"),
    promoTotal: sumMoney(promos.map(({ amount }) => amount)),
  };
}
